import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Trash2, Heart, Gift, Globe2, Filter } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { adminApi } from "@/lib/adminApi";
import AdminPageHeader from "@/components/admin/AdminPageHeader";

function fmtDate(iso) {
    try {
        return new Intl.DateTimeFormat("de-AT", { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
    } catch {
        return iso;
    }
}

function formatEur(n) {
    try {
        return new Intl.NumberFormat("de-AT", { style: "currency", currency: "EUR" }).format(n || 0);
    } catch {
        return `€${n}`;
    }
}

const areaLabels = {
    general: "General",
    health: "Health",
    education: "Education",
    culture: "Culture",
    social: "Social",
};

export default function AdminDonations() {
    const [data, setData] = useState({ items: [], total: 0, page_sum: 0 });
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [filterType, setFilterType] = useState("all");
    const [filterFreq, setFilterFreq] = useState("all");

    const params = useMemo(() => {
        const p = { limit: 200 };
        if (filterType !== "all") p.donation_type = filterType;
        if (filterFreq !== "all") p.frequency = filterFreq;
        return p;
    }, [filterType, filterFreq]);

    const load = () => {
        setLoading(true);
        adminApi
            .donations(params)
            .then(setData)
            .catch(() => toast.error("Failed to load"))
            .finally(() => setLoading(false));
    };
    useEffect(load, [params]);

    const onDelete = async (id) => {
        setDeleting(id);
        try {
            await adminApi.deleteDonation(id);
            toast.success("Deleted");
            setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== id), total: d.total - 1 }));
        } catch {
            toast.error("Failed to delete");
        } finally {
            setDeleting(null);
        }
    };

    const onExport = async () => {
        setExporting(true);
        try {
            await adminApi.exportCsv("donations");
        } catch {
            toast.error("Export failed");
        } finally {
            setExporting(false);
        }
    };

    return (
        <div data-testid="admin-donations">
            <AdminPageHeader
                eyebrow="Admin · Fundraising"
                title="Donations"
                count={data.total}
                onRefresh={load}
                onExport={onExport}
                exporting={exporting}
            >
                <div className="mt-3 flex flex-wrap items-center gap-2">
                    <Filter className="w-3.5 h-3.5 text-caireen-ink/50" />
                    <Select value={filterType} onValueChange={setFilterType}>
                        <SelectTrigger data-testid="donations-filter-type" className="h-9 w-44 rounded-full bg-white border-caireen-ink/15 font-sans text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All types</SelectItem>
                            <SelectItem value="regular">Regular</SelectItem>
                            <SelectItem value="gift">Gift</SelectItem>
                        </SelectContent>
                    </Select>
                    <Select value={filterFreq} onValueChange={setFilterFreq}>
                        <SelectTrigger data-testid="donations-filter-freq" className="h-9 w-44 rounded-full bg-white border-caireen-ink/15 font-sans text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All frequencies</SelectItem>
                            <SelectItem value="once">One-time</SelectItem>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="yearly">Yearly</SelectItem>
                        </SelectContent>
                    </Select>
                    <span className="ml-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-caireen-orange/10 text-caireen-orange font-sans text-xs font-semibold">
                        Page sum: {formatEur(data.page_sum)}
                    </span>
                </div>
            </AdminPageHeader>

            <div className="bg-white rounded-3xl ring-1 ring-caireen-ink/10 overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center py-24 text-caireen-ink/40">
                        <Loader2 className="w-5 h-5 animate-spin" />
                    </div>
                ) : data.items.length === 0 ? (
                    <div className="text-center py-20 font-sans text-sm text-caireen-ink/55">
                        <Heart className="w-8 h-8 mx-auto mb-3 text-caireen-ink/30" />
                        No donations yet.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-caireen-cream/60">
                                    <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">When</TableHead>
                                    <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Donor</TableHead>
                                    <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55 text-right">Amount</TableHead>
                                    <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Type</TableHead>
                                    <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Frequency</TableHead>
                                    <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Area</TableHead>
                                    <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55">Recipient</TableHead>
                                    <TableHead className="font-sans text-xs uppercase tracking-widest text-caireen-ink/55 text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {data.items.map((d) => (
                                    <TableRow key={d.id} className="hover:bg-caireen-cream/40">
                                        <TableCell className="font-sans text-sm text-caireen-ink/70 whitespace-nowrap">{fmtDate(d.created_at)}</TableCell>
                                        <TableCell>
                                            <p className="font-sans text-sm font-semibold">{d.donor_name}</p>
                                            <p className="font-sans text-xs text-caireen-ink/55">{d.donor_email}</p>
                                        </TableCell>
                                        <TableCell className="text-right font-serif text-lg text-caireen-orange whitespace-nowrap">
                                            {formatEur(d.amount)}
                                        </TableCell>
                                        <TableCell>
                                            {d.donation_type === "gift" ? (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-caireen-yellow/20 font-sans text-[10px] uppercase tracking-widest">
                                                    <Gift className="w-3 h-3" />
                                                    Gift
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-caireen-orange/10 text-caireen-orange font-sans text-[10px] uppercase tracking-widest">
                                                    <Heart className="w-3 h-3" />
                                                    Regular
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell className="font-sans text-sm text-caireen-ink/70 capitalize">{d.frequency}</TableCell>
                                        <TableCell className="font-sans text-sm text-caireen-ink/70">{areaLabels[d.dedicated_area] || d.dedicated_area}</TableCell>
                                        <TableCell className="font-sans text-sm text-caireen-ink/70">
                                            {d.donation_type === "gift" && d.recipient_name ? (
                                                <span className="inline-flex items-center gap-1.5">
                                                    <Globe2 className="w-3.5 h-3.5 text-caireen-ink/40" />
                                                    {d.recipient_name}
                                                </span>
                                            ) : (
                                                "—"
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <Button
                                                data-testid={`donation-delete-${d.id}`}
                                                variant="ghost"
                                                size="sm"
                                                disabled={deleting === d.id}
                                                onClick={() => onDelete(d.id)}
                                                className="text-caireen-red hover:bg-caireen-red/10 rounded-full"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>
        </div>
    );
}
